import { CAR_BY_ID } from './cars.js';
import { isWeather } from './types.js';
import type {
  ArcadePreset,
  CrewModule,
  NpcDifficulty,
  RaceEventMode,
  RacePhase,
  RaceSettings,
  RivalStyle,
  TimeOfDay,
  VisualTuning,
} from './types.js';
import type { PersistedRoomState } from './race-session-state.js';

const IDENTIFIER = /^[a-zA-Z0-9_-]{1,100}$/;
const MAP_IDENTIFIER = /^[a-z0-9-]{1,100}$/;
const RACE_HISTORY_KEY = /^[1-9]\d{0,2}:(?:[a-zA-Z0-9_-]{1,100}:)?[a-zA-Z0-9_-]{1,100}$/;
const TOKEN = /^(?:[a-zA-Z0-9_-]{8,200}|sha256:[a-f0-9]{64})$/;
const HEX = /^#[0-9a-f]{6}$/i;
const PHASES = new Set<RacePhase>(['lobby', 'countdown', 'racing', 'finished']);
const DIFFICULTIES = new Set<NpcDifficulty>(['rookie', 'sport', 'pro']);
const EVENT_MODES = new Set<RaceEventMode>([
  'circuit',
  'sprint',
  'time-trial',
  'style-rush',
  'elimination',
  'heat-run',
  'road-rage',
]);
const TIMES = new Set<TimeOfDay>(['dawn', 'day', 'sunset', 'night']);
const ARCADE_PRESETS = new Set<ArcadePreset>([
  'standard',
  'nitro-rush',
  'heavy-metal',
  'traffic-panic',
  'low-gravity',
]);
const CREW_MODULES = new Set<CrewModule>(['balanced', 'grip', 'nitro', 'armour']);
const RIVAL_STYLES = new Set<RivalStyle>(['grip', 'drift', 'bully', 'sprinter', 'trickster']);
const SPOILERS = new Set<VisualTuning['spoiler']>(['none', 'ducktail', 'gt']);
const WHEELS = new Set<VisualTuning['wheels']>(['turbine', 'mesh', 'five-spoke']);
const DECALS = new Set<VisualTuning['decal']>([
  'none',
  'center-stripe',
  'twin-stripe',
  'chevron',
  'circuit',
]);
const POST_RACE_VOTES = new Set(['rematch', 'next-map', 'surprise']);
export const PERSISTED_ROOM_STATE_VERSION = 2;

const REQUIRED_RACER_NUMBERS = [
  'lastInputSequence',
  'lastProcessedInputSequence',
  'resumeAllowedUntil',
  'aiSeed',
  'aiLaneOffset',
  'aiNextControlAt',
  'aiTargetSpeed',
  'aiWallHits',
  'aiOvertakes',
  'aiRouteUses',
  'aiLineProgress',
  'aiStuckSince',
  'aiFocusUntil',
  'x',
  'y',
  'groundY',
  'z',
  'yaw',
  'travelYaw',
  'turretYaw',
  'speed',
  'velocityX',
  'velocityZ',
  'verticalSpeed',
  'steer',
  'drift',
  'engineRpm',
  'gear',
  'shiftTimer',
  'wheelSlip',
  'longitudinalAcceleration',
  'lateralAcceleration',
  'chassisPitch',
  'chassisRoll',
  'pitchVelocity',
  'rollVelocity',
  'angularVelocity',
  'nitroCharge',
  'trackSegment',
  'previousX',
  'previousZ',
  'previousYaw',
  'styleScore',
  'styleCombo',
  'styleEventId',
  'styleExpiresAt',
  'driftStyleTime',
  'draftStyleTime',
  'draftCharge',
  'draftBoostUntil',
  'airtime',
  'lastPlace',
  'lastOvertakeAt',
  'health',
  'wreckedUntil',
  'waterResetAt',
  'lastFireAt',
  'rampCooldownUntil',
  'stuntLoopProgress',
  'stuntLoopSpeed',
  'stuntLoopCooldownUntil',
  'lap',
  'checkpoint',
  'progress',
] as const;

const REQUIRED_RACER_BOOLEANS = [
  'npc',
  'pursuit',
  'pursuitActive',
  'ready',
  'connected',
  'hasConnected',
  'aiRouteInProgress',
  'nitroActive',
  'handbrakeWasActive',
  'wasAirborne',
  'eliminated',
] as const;

/** Runtime boundary for JSON descriptors. TypeScript types do not protect disk data. */
export function parsePersistedRoomState(
  value: unknown,
  expectedRoomId?: string,
): PersistedRoomState | null {
  value = migrateLegacyPersistedRoomState(value);
  if (!isRecord(value) || value.version !== PERSISTED_ROOM_STATE_VERSION) return null;
  if (!identifier(value.id) || (expectedRoomId !== undefined && value.id !== expectedRoomId)) return null;
  if (
    !token(value.inviteToken)
    || !identifier(value.hostPlayerId)
    || !validRaceSettings(value.settings)
    || !finite(value.createdAt)
    || !finite(value.expiresAt)
    || !finite(value.lastActivity)
    || !PHASES.has(value.phase as RacePhase)
    || !finite(value.countdownUntil)
    || !optionalFiniteOrNull(value.raceDeadlineAt)
    || !optionalFiniteOrNull(value.finishWindowUntil)
    || !optionalFiniteOrNull(value.finishedWallAt)
    || !nonNegativeInteger(value.sequence)
    || !nonNegativeInteger(value.projectileSequence)
    || !nonNegativeInteger(value.impactSequence)
    || !(value.finishedAt === null || finite(value.finishedAt))
    || !optionalPositiveInteger(value.raceAttempt)
    || !optionalFinite(value.offlineClockAt)
    || !optionalFinite(value.simulationNow)
    || !optionalFinite(value.dynamicTrackEventNextAt)
    || !optionalFinite(value.dynamicTrackEventPhaseEndsAt)
    || !optionalNonNegativeInteger(value.dynamicTrackEventSequence)
    || !(value.roadRageTargetId === undefined
      || value.roadRageTargetId === null
      || identifier(value.roadRageTargetId))
    || !(value.successorRoomId === undefined
      || (identifier(value.successorRoomId) && value.successorRoomId !== value.id))
    || !(value.predecessorRoomId === undefined
      || (identifier(value.predecessorRoomId) && value.predecessorRoomId !== value.id))
    || !(value.successorInviteTokens === undefined
      || (
        Array.isArray(value.successorInviteTokens)
        && value.successorInviteTokens.length >= 1
        && value.successorInviteTokens.length <= 4
        && value.successorInviteTokens.every(token)
      ))
    || (value.successorInviteTokens !== undefined && value.successorRoomId === undefined)
    || !(value.bannedKeys === undefined
      || (
        Array.isArray(value.bannedKeys)
        && value.bannedKeys.length <= 256
        && value.bannedKeys.every((key) => typeof key === 'string' && /^[a-f0-9]{16,128}$/.test(key))
      ))
    || (value.predecessorRoomId !== undefined && value.successorRoomId !== undefined)
    || !optionalArray(value.postRaceVotes)
    || (Array.isArray(value.postRaceVotes) && value.postRaceVotes.length > 12)
    || !(value.postRaceDecision === undefined
      || value.postRaceDecision === null
      || POST_RACE_VOTES.has(String(value.postRaceDecision)))
    || !optionalFinite(value.trafficTimeMs)
    || !Array.isArray(value.racers)
    || value.racers.length < 1
    // Up to 6 humans (settings.maxPlayers) + 11 NPCs + 2 pursuit units can share one room.
    || value.racers.length > 19
    || !Array.isArray(value.projectiles)
    || value.projectiles.length > 128
    || !Array.isArray(value.impacts)
    || value.impacts.length > 256
    || !optionalArray(value.trafficPhysics)
    || !validDynamicTrackEvent(value.dynamicTrackEvent)
  ) return null;

  const racerIds = new Set<string>();
  const humanRacerIds = new Set<string>();
  let humanHostFound = false;
  for (const racer of value.racers) {
    if (!validPersistedRacer(racer) || racerIds.has(racer.id)) return null;
    racerIds.add(racer.id);
    if (racer.npc === false) humanRacerIds.add(racer.id);
    if (racer.id === value.hostPlayerId && racer.npc === false) humanHostFound = true;
  }
  if (!humanHostFound) return null;
  if (typeof value.roadRageTargetId === 'string' && !racerIds.has(value.roadRageTargetId)) return null;
  if (Array.isArray(value.postRaceVotes)) {
    const voterIds = new Set<string>();
    for (const entry of value.postRaceVotes) {
      if (
        !isRecord(entry)
        || !identifier(entry.playerId)
        || !humanRacerIds.has(entry.playerId)
        || voterIds.has(entry.playerId)
        || !POST_RACE_VOTES.has(String(entry.vote))
      ) return null;
      voterIds.add(entry.playerId);
    }
  }
  if (!value.projectiles.every(validProjectile) || !value.impacts.every(validImpact)) return null;
  if (
    Array.isArray(value.trafficPhysics)
    && !value.trafficPhysics.every((entry) => (
      isRecord(entry)
      && identifier(entry.id)
      && finite(entry.offsetX)
      && finite(entry.offsetZ)
      && finite(entry.velocityX)
      && finite(entry.velocityZ)
      && finite(entry.yawOffset)
      && finite(entry.angularVelocity)
    ))
  ) return null;
  if (!deepNumbersAreFinite(value)) return null;

  return value as unknown as PersistedRoomState;
}

/**
 * Version 1 was kept in use while runtime racer fields were added. Restore
 * already had safe defaults for those fields, but the strict disk parser ran
 * first and made those fallbacks unreachable. Normalize only missing legacy
 * values here; malformed values remain malformed and are still rejected below.
 */
function migrateLegacyPersistedRoomState(value: unknown): unknown {
  if (!isRecord(value) || value.version !== 1) return value;
  const migrated: Record<string, unknown> = {
    ...value,
    version: PERSISTED_ROOM_STATE_VERSION,
  };
  if (Array.isArray(value.projectiles)) {
    migrated.projectiles = value.projectiles.map((candidate) => {
      if (!isRecord(candidate) || candidate.verticalSpeed !== undefined) return candidate;
      return { ...candidate, verticalSpeed: 0 };
    });
  }
  if (!Array.isArray(value.racers)) return migrated;
  migrated.racers = value.racers.map((candidate) => {
    if (!isRecord(candidate)) return candidate;
    const racer: Record<string, unknown> = { ...candidate };
    const yaw = finite(racer.yaw) ? racer.yaw : 0;
    const speed = finite(racer.speed) ? racer.speed : 0;
    const positionY = finite(racer.y) ? racer.y : 0;
    const numberDefaults: Record<string, number> = {
      lastInputSequence: -1,
      lastProcessedInputSequence: -1,
      resumeAllowedUntil: 0,
      aiSeed: 0,
      aiLaneOffset: 0,
      aiNextControlAt: 0,
      aiTargetSpeed: 0,
      aiWallHits: 0,
      aiOvertakes: 0,
      aiRouteUses: 0,
      aiLineProgress: 0,
      aiStuckSince: 0,
      aiFocusUntil: 0,
      x: 0,
      y: positionY,
      groundY: positionY,
      z: 0,
      yaw,
      travelYaw: yaw,
      turretYaw: 0,
      speed,
      velocityX: Math.sin(yaw) * speed,
      velocityZ: Math.cos(yaw) * speed,
      verticalSpeed: 0,
      steer: 0,
      drift: 0,
      engineRpm: 900,
      gear: 1,
      shiftTimer: 0,
      wheelSlip: 0,
      longitudinalAcceleration: 0,
      lateralAcceleration: 0,
      chassisPitch: 0,
      chassisRoll: 0,
      pitchVelocity: 0,
      rollVelocity: 0,
      angularVelocity: 0,
      nitroCharge: 50,
      trackSegment: 0,
      previousX: finite(racer.x) ? racer.x : 0,
      previousZ: finite(racer.z) ? racer.z : 0,
      previousYaw: yaw,
      styleScore: 0,
      styleCombo: 0,
      styleEventId: 0,
      styleExpiresAt: 0,
      driftStyleTime: 0,
      draftStyleTime: 0,
      draftCharge: 0,
      draftBoostUntil: 0,
      airtime: 0,
      lastPlace: 1,
      lastOvertakeAt: 0,
      health: 100,
      wreckedUntil: 0,
      waterResetAt: 0,
      lastFireAt: 0,
      rampCooldownUntil: 0,
      stuntLoopProgress: 0,
      stuntLoopSpeed: 0,
      stuntLoopCooldownUntil: 0,
      lap: 1,
      checkpoint: 1,
      progress: 0,
    };
    for (const field of REQUIRED_RACER_NUMBERS) {
      if (racer[field] === undefined) racer[field] = numberDefaults[field] ?? 0;
    }

    const booleanDefaults: Record<string, boolean> = {
      npc: false,
      pursuit: false,
      pursuitActive: false,
      ready: false,
      connected: false,
      hasConnected: false,
      aiRouteInProgress: false,
      nitroActive: false,
      handbrakeWasActive: false,
      wasAirborne: false,
      eliminated: false,
    };
    for (const field of REQUIRED_RACER_BOOLEANS) {
      if (racer[field] === undefined) racer[field] = booleanDefaults[field] ?? false;
    }

    if (racer.crewModule === undefined) racer.crewModule = 'balanced';
    if (racer.rivalStyle === undefined) racer.rivalStyle = null;
    for (const field of [
      'aiTargetRouteId',
      'aiFocusId',
      'draftTargetId',
      'sectorOpponentId',
      'lastSectorOpponentId',
      'revengeTargetId',
      'stuntLoopId',
    ]) {
      if (racer[field] === undefined) racer[field] = null;
    }
    if (racer.aiActiveLineId === undefined) racer.aiActiveLineId = 'main';
    if (racer.aiRouteHistory === undefined) racer.aiRouteHistory = [];
    if (racer.passedRiskGates === undefined) racer.passedRiskGates = [];
    if (racer.disconnectedAt === undefined) racer.disconnectedAt = null;
    if (racer.finishedAt === undefined) racer.finishedAt = null;
    if (racer.styleAction === undefined) racer.styleAction = '';

    if (racer.input === undefined) {
      racer.input = {
        throttle: 0,
        brake: 0,
        steer: 0,
        handbrake: false,
        nitro: false,
        turret: 0,
        fire: false,
        reset: false,
        bank: false,
        sequence: finite(racer.lastProcessedInputSequence) ? racer.lastProcessedInputSequence : -1,
      };
    }
    if (racer.launchControl === undefined) {
      racer.launchControl = { charge: 0, sweetTime: 0, overTime: 0, sampledTime: 0 };
    }
    const cornerDefaults = {
      active: false,
      elapsed: 0,
      straightTime: 0,
      peakCurvature: 0,
      previousCurvature: 0,
      entryBrake: 0,
      exitThrottle: 0,
      minimumSpeed: 0,
      clean: true,
    };
    if (racer.cornerMastery === undefined) {
      racer.cornerMastery = cornerDefaults;
    } else if (isRecord(racer.cornerMastery)) {
      const corner = { ...cornerDefaults, ...racer.cornerMastery };
      if (corner.minimumSpeed === null) corner.minimumSpeed = 0;
      racer.cornerMastery = corner;
    }
    return racer;
  });
  return migrated;
}

function validRaceSettings(value: unknown): value is RaceSettings {
  if (!isRecord(value)) return false;
  return typeof value.mapId === 'string'
    && MAP_IDENTIFIER.test(value.mapId)
    && integerInRange(value.npcCount, 0, 11)
    && DIFFICULTIES.has(value.npcDifficulty as NpcDifficulty)
    && integerInRange(value.maxPlayers, 1, 12)
    && optionalBoolean(value.antiCheat)
    && optionalBoolean(value.collisions)
    && optionalBoolean(value.traffic)
    && (value.timeOfDay === undefined || TIMES.has(value.timeOfDay as TimeOfDay))
    && (value.weather === undefined || isWeather(value.weather))
    && optionalBoolean(value.timeTrial)
    && (value.eventMode === undefined || EVENT_MODES.has(value.eventMode as RaceEventMode))
    && (value.arcadePreset === undefined || ARCADE_PRESETS.has(value.arcadePreset as ArcadePreset))
    && (value.tourId === undefined || (
      typeof value.tourId === 'string'
      && /^[a-z0-9-]{1,64}$/i.test(value.tourId)
    ))
    && (value.tourStage === undefined || integerInRange(value.tourStage, 0, 31))
    && (value.featuredRival === undefined || shortText(value.featuredRival, 24))
    && (value.wager === undefined
      || ['clean-finish', 'clean-passes', 'extreme-route'].includes(String(value.wager)))
    && (value.dailyChallengeId === undefined
      || (typeof value.dailyChallengeId === 'string'
        && validUtcDateId(value.dailyChallengeId)))
    && (value.rewindLimit === undefined || integerInRange(value.rewindLimit, 0, 5))
    && (value.npcRoster === undefined || (
      Array.isArray(value.npcRoster)
      && value.npcRoster.length <= 11
      && value.npcRoster.every((entry) => DIFFICULTIES.has(entry as NpcDifficulty))
    ))
    && (value.visibility === undefined || value.visibility === 'private' || value.visibility === 'public')
    && (value.laps === undefined || integerInRange(value.laps, 1, 10));
}

function validUtcDateId(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function validPersistedRacer(value: unknown): value is Record<string, unknown> & {
  id: string;
  npc: boolean;
} {
  if (
    !isRecord(value)
    || !identifier(value.id)
    || typeof value.name !== 'string'
    || value.name.trim().length < 1
    || value.name.length > 24
    || typeof value.carId !== 'string'
    || !CAR_BY_ID.has(value.carId)
    || typeof value.npc !== 'boolean'
    || !(value.npc && value.token === '') && !token(value.token)
    || !validTuning(value.tuning)
    || !CREW_MODULES.has(value.crewModule as CrewModule)
    || !(value.rivalStyle === null || RIVAL_STYLES.has(value.rivalStyle as RivalStyle))
    || !nullableIdentifier(value.aiTargetRouteId)
    || typeof value.aiActiveLineId !== 'string'
    || value.aiActiveLineId.length > 100
    || !nullableIdentifier(value.aiFocusId)
    || !nullableIdentifier(value.draftTargetId)
    || !nullableIdentifier(value.sectorOpponentId)
    || !nullableIdentifier(value.lastSectorOpponentId)
    || !nullableIdentifier(value.revengeTargetId)
    || !nullableIdentifier(value.stuntLoopId)
    || !Array.isArray(value.aiRouteHistory)
    || !value.aiRouteHistory.every(raceHistoryKey)
    || !Array.isArray(value.passedRiskGates)
    || !value.passedRiskGates.every(raceHistoryKey)
    || !(value.disconnectedAt === null || finite(value.disconnectedAt))
    || !optionalFinite(value.partyTransferUntil)
    || !(value.finishedAt === null || finite(value.finishedAt))
  ) return false;
  for (const field of REQUIRED_RACER_NUMBERS) {
    if (!finite(value[field])) return false;
  }
  for (const field of REQUIRED_RACER_BOOLEANS) {
    if (typeof value[field] !== 'boolean') return false;
  }
  return validPersistedInput(value.input)
    && validLaunchControl(value.launchControl)
    && validCornerMastery(value.cornerMastery);
}

function validPersistedInput(value: unknown): boolean {
  return isRecord(value)
    && numberInRange(value.throttle, 0, 1)
    && numberInRange(value.brake, 0, 1)
    && numberInRange(value.steer, -1, 1)
    && typeof value.handbrake === 'boolean'
    && typeof value.nitro === 'boolean'
    && numberInRange(value.turret, -1, 1)
    && typeof value.fire === 'boolean'
    && typeof value.reset === 'boolean'
    && optionalBoolean(value.bank)
    && Number.isSafeInteger(value.sequence)
    && (value.sequence as number) >= -1;
}

function validLaunchControl(value: unknown): boolean {
  return isRecord(value)
    && numberInRange(value.charge, 0, 1)
    && nonNegativeFinite(value.sweetTime)
    && nonNegativeFinite(value.overTime)
    && numberInRange(value.sampledTime, 0, 1.4);
}

function validCornerMastery(value: unknown): boolean {
  return isRecord(value)
    && typeof value.active === 'boolean'
    && nonNegativeFinite(value.elapsed)
    && nonNegativeFinite(value.straightTime)
    && nonNegativeFinite(value.peakCurvature)
    && nonNegativeFinite(value.previousCurvature)
    && numberInRange(value.entryBrake, 0, 1)
    && numberInRange(value.exitThrottle, 0, 1)
    && nonNegativeFinite(value.minimumSpeed)
    && typeof value.clean === 'boolean';
}

function validTuning(value: unknown): value is VisualTuning {
  if (!isRecord(value)) return false;
  return HEX.test(String(value.paint))
    && HEX.test(String(value.accent))
    && HEX.test(String(value.rims))
    && HEX.test(String(value.neon))
    && typeof value.neonEnabled === 'boolean'
    && SPOILERS.has(value.spoiler as VisualTuning['spoiler'])
    && WHEELS.has(value.wheels as VisualTuning['wheels'])
    && DECALS.has(value.decal as VisualTuning['decal'])
    && finite(value.stance)
    && Math.abs(value.stance as number) <= 0.08;
}

function validProjectile(value: unknown): boolean {
  return isRecord(value)
    && identifier(value.id)
    && identifier(value.ownerId)
    && finite(value.x)
    && finite(value.y)
    && finite(value.z)
    && finite(value.yaw)
    && finite(value.speed)
    && finite(value.verticalSpeed)
    && finite(value.createdAt);
}

function validImpact(value: unknown): boolean {
  return isRecord(value)
    && identifier(value.id)
    && finite(value.x)
    && finite(value.y)
    && finite(value.z)
    && finite(value.createdAt)
    && ['collision', 'shell', 'water'].includes(String(value.kind))
    && finite(value.severity)
    && Array.isArray(value.racerIds)
    && value.racerIds.every(identifier)
    && (
      value.responses === undefined
      || (
        Array.isArray(value.responses)
        && value.responses.every((response) => (
          isRecord(response)
          && identifier(response.racerId)
          && finite(response.deltaVelocityX)
          && finite(response.deltaVelocityZ)
          && finite(response.deltaAngularVelocity)
          && optionalFinite(response.deltaPitchVelocity)
          && optionalFinite(response.deltaRollVelocity)
        ))
      )
    );
}

function validDynamicTrackEvent(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  return isRecord(value)
    && identifier(value.id)
    && ['lane-closure', 'street-crossing', 'roadblock'].includes(String(value.kind))
    && ['warning', 'active', 'clearing'].includes(String(value.phase))
    && finite(value.deployment)
    && integerInRange(value.trackIndex, 0, 10_000)
    && (value.side === 'left' || value.side === 'right')
    && finite(value.remainingMs)
    && nonNegativeInteger(value.eventId);
}

function deepNumbersAreFinite(value: unknown): boolean {
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(deepNumbersAreFinite);
  if (!isRecord(value)) return true;
  return Object.values(value).every(deepNumbersAreFinite);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function identifier(value: unknown): value is string {
  return typeof value === 'string' && IDENTIFIER.test(value);
}

function raceHistoryKey(value: unknown): value is string {
  return typeof value === 'string' && RACE_HISTORY_KEY.test(value);
}

function nullableIdentifier(value: unknown): boolean {
  return value === null || identifier(value);
}

function token(value: unknown): value is string {
  return typeof value === 'string' && TOKEN.test(value);
}

function shortText(value: unknown, maximum: number): boolean {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= maximum;
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function nonNegativeFinite(value: unknown): value is number {
  return finite(value) && value >= 0;
}

function numberInRange(value: unknown, minimum: number, maximum: number): value is number {
  return finite(value) && value >= minimum && value <= maximum;
}

function optionalFinite(value: unknown): boolean {
  return value === undefined || finite(value);
}

function optionalFiniteOrNull(value: unknown): boolean {
  return value === undefined || value === null || finite(value);
}

function optionalBoolean(value: unknown): boolean {
  return value === undefined || typeof value === 'boolean';
}

function optionalArray(value: unknown): boolean {
  return value === undefined || Array.isArray(value);
}

function nonNegativeInteger(value: unknown): boolean {
  return Number.isInteger(value) && (value as number) >= 0;
}

function optionalNonNegativeInteger(value: unknown): boolean {
  return value === undefined || nonNegativeInteger(value);
}

function optionalPositiveInteger(value: unknown): boolean {
  return value === undefined || (Number.isInteger(value) && (value as number) >= 1);
}

function integerInRange(value: unknown, minimum: number, maximum: number): boolean {
  return Number.isInteger(value) && (value as number) >= minimum && (value as number) <= maximum;
}
